import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { KeyboardNavigationKey, ListValue } from '../stateful-list.types';

import { useFocusableItems, UseFocusableItemsProps } from './use-focusable-items';

type UseKeyboardNavigationProps = UseFocusableItemsProps & {
  /** Callback for each item, except select all */
  onItemSelect?: (item: ListValue) => void;
};

/** Hook designed to provide keyboard navigation within the stateful list */
export function useKeyboardNavigation(props: UseKeyboardNavigationProps) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const items = useFocusableItems(props);
  const activeDescendantId = useMemo(() => {
    if (focusedIndex == null) return undefined;
    return items[focusedIndex]?.id;
  }, [focusedIndex, items]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (items.length === 0) return;

      // Ignore key events when focused on input or textarea elements
      // Otherwise it would interfere with typing
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (event.key) {
        case KeyboardNavigationKey.Tab:
        case KeyboardNavigationKey.ArrowDown:
          event.preventDefault();
          event.stopPropagation();
          setFocusedIndex((prev) => (prev === null ? 0 : (prev + 1) % items.length));
          break;

        case KeyboardNavigationKey.ArrowUp:
          event.preventDefault();
          event.stopPropagation();
          setFocusedIndex((prev) =>
            prev === null ? items.length - 1 : (prev - 1 + items.length) % items.length
          );
          break;
        case KeyboardNavigationKey.Enter:
        case KeyboardNavigationKey.Space:
          event.preventDefault();
          event.stopPropagation();
          if (focusedIndex !== null) {
            const item = items[focusedIndex];
            item.onItemSelect?.(item.value);
          }
          break;
        default:
          // Ignore other keys
          return;
      }
    },
    [items, focusedIndex]
  );

  // Scroll and focus active item when it changes
  useEffect(() => {
    if (!activeDescendantId || !containerRef.current) return;
    const el = containerRef.current.querySelector<HTMLElement>(
      `#${CSS.escape(activeDescendantId)}`
    );
    if (el) {
      el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      el.focus();
    }
  }, [activeDescendantId]);

  return {
    containerRef,
    onKeyDown,
    focusedIndex,
    activeDescendantId,
  };
}
